/* Canonical Task Detail panel (S3 / benchmark §2.3 — the #1 parity gap).
 *
 * The product's central noun finally gets an openable surface. A right-side
 * drawer over the work surfaces, opened identically from a board list row, a
 * kanban card, and the EXISTING `?task=<id>` deep-link (ws/taskLink.ts's
 * revealRow now calls openTaskPanel instead of the legacy inline expander).
 * One implementation, one mounted host, re-rendered live on every ws change.
 *
 * SACRED INVARIANTS (do not regress):
 *  - Verified is EVIDENCE-ONLY. This panel never offers a click that stamps
 *    Verified/Regressed — `ws.transitions[status]` (the server's legal map)
 *    never contains them, and the panel renders ONLY that map. The verification
 *    affordances are: (a) run a recheck probe (the existing deterministic
 *    `ws.recheck`), and (b) the human "Attested" flow — a dialog with a
 *    MANDATORY non-empty evidence note (the exact `ask()` statusMenu uses),
 *    which the server records distinctly and the pill renders as the dashed-
 *    green attested pill (`.wsst.s-attested`), never the probe-verified pill.
 *  - Status changes go through the ONE guarded path: `applyTransition`
 *    (ws/board.ts) — optimistic paint + Undo toast + rollback, evidence-gate /
 *    stall-clock all server-side. This module NEVER forks it; the small
 *    per-target wrapper below only collects the wont-do reason / attested note
 *    exactly as statusMenu does, then defers to `applyTransition`.
 *  - Legal-only: a 422/409 from the server surfaces as an honest warn toast
 *    (applyTransition's own rollback), never silently.
 *  - Checking a checklist item NEVER transitions status (server invariant C1;
 *    the reused checklistPanel honours it).
 *  - Stall clock (`status_changed_at`) is never touched by a panel render.
 *
 * DATA SOURCES:
 *  - the task row from `ws.tasks` when the board carries it (resolved fresh each
 *    render so an optimistic status paint / refresh() flows straight through),
 *    falling back to the single-task read `GET /clients/{slug}/tasks/{id}` (gap
 *    1) so a cross-client My-Work / deep-link open renders even when ws.tasks
 *    doesn't include the row;
 *  - `GET /tasks/{id}/events` — the full activity trail (status / note /
 *    recheck / reconcile), which also carries the probe history and reconcile
 *    provenance rendered in the Evidence block;
 *  - `GET /clients/{slug}/tasks/{id}/watchers` (gap 3) — the active-watcher
 *    roster rendered as NAMES beside the eye toggle's count;
 *  - `ws.fetchChecklist(t)` — the full checklist for the reused editor;
 *  - `ws.notes` (already client-scoped, fetched in init) filtered to this task.
 * Editable content (gap 2): a CUSTOM task's title/detail edit inline; a DERIVED
 * task's title/detail are derivation-owned (read-only) and carry an AM
 * annotation (detail_note) instead — every write rides PATCH /tasks/{id}, whose
 * 422/403 surfaces as an honest warn toast. A relations graph + attachments
 * remain honestly out of scope (not faked).
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { applyTransition } from './board.ts';
import { labelEditor } from './labels.ts';
import { checklistPanel } from './checklist.ts';
import { mentionBlur, mentionInput, mentionKeydown, renderMentionBody } from './mentions.ts';
import { payloadStore } from '../state/payload.ts';
import { wsChangeStore } from '../state/wsChange.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsTask, WsChecklistItem, WsTaskEvent, WsNote, WsWatcher } from '../types/workspace.ts';
import '../styles/taskdetail.css';

// ── module state ──────────────────────────────────────────────────────────
let openTaskId: number | null = null;
let currentWs: Workspace | null = null;
// last-known task object, so a render survives a transient refresh() that
// momentarily drops the row before the next fetch re-adds it. Also the landing
// spot for the single-task read (gap 1) so a cross-client / deep-link open
// renders even when the board list (ws.tasks) doesn't carry the row.
let lastTask: WsTask | null = null;
// async caches, keyed by task id (cleared on open so a re-open re-fetches).
const eventsCache = new Map<number, WsTaskEvent[]>();
const checklistCache = new Map<number, WsChecklistItem[]>();
// gap 3: the active-watcher roster (identities), keyed by task id.
const watchersCache = new Map<number, WsWatcher[]>();

// ── DOM host (created once, reused) ─────────────────────────────────────────
interface Host { scrim: HTMLElement; body: HTMLElement }
let host: Host | null = null;

function ensureHost(): Host {
  if (host) return host;
  const scrim = document.createElement('div');
  scrim.className = 'td-scrim';
  scrim.setAttribute('data-td-scrim', '');
  const panel = document.createElement('aside');
  panel.className = 'td-panel';
  panel.setAttribute('role', 'dialog');
  // aria-modal=false: this is a WORKING surface, not a blocking modal — the
  // live board keeps updating behind it (the scrim is faint, not a black-out).
  panel.setAttribute('aria-modal', 'false');
  panel.setAttribute('aria-label', 'Task detail');
  const body = document.createElement('div');
  body.className = 'td-body';
  body.setAttribute('data-td-body', '');
  panel.appendChild(body);
  scrim.appendChild(panel);
  // outside-click: only a DIRECT click on the scrim backdrop closes — a click
  // on a spawned .modal-bk / .wstoast (siblings under <body>, not descendants
  // of the scrim) never reaches this listener, so a sub-dialog can't self-close
  // the panel (same discipline as ask()'s `if (e.target === bk)`).
  scrim.addEventListener('mousedown', (e) => { if (e.target === scrim) closeTaskPanel(); });
  document.body.appendChild(scrim);
  host = { scrim, body };
  return host;
}

// document-level Escape — but only when no sub-dialog/menu is open (Escape in
// an ask() modal or a wsmenu must close THAT first, not the panel underneath).
function onKeydown(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  if (openTaskId == null) return;
  if (document.querySelector('.modal-bk, .wsmenu')) return;
  closeTaskPanel();
}

// live re-render: any ws data change (optimistic status paint, refresh(),
// setField, label/checklist/watch write) re-renders the open panel with the
// fresh task. Subscribed once at module load; a no-op while nothing is open.
wsChangeStore.subscribe(() => {
  if (openTaskId != null && currentWs) renderPanel(currentWs);
});

// ── open / close ────────────────────────────────────────────────────────────
export function openTaskPanel(ws: Workspace, t: WsTask): void {
  currentWs = ws;
  openTaskId = t.id;
  lastTask = t;
  eventsCache.delete(t.id);
  checklistCache.delete(t.id);
  watchersCache.delete(t.id);
  const h = ensureHost();
  h.scrim.classList.add('open');
  document.addEventListener('keydown', onKeydown);
  renderPanel(ws);
  void loadDetail(ws, t.id);
}

export function closeTaskPanel(): void {
  openTaskId = null;
  lastTask = null;
  document.removeEventListener('keydown', onKeydown);
  if (host) {
    host.scrim.classList.remove('open');
    render(nothing, host.body);
  }
}

export function isTaskPanelOpen(): boolean { return openTaskId != null; }

// fetch the async detail (events + watcher roster + full checklist) then
// re-render.
async function loadDetail(ws: Workspace, id: number): Promise<void> {
  try {
    const r = await ws.api<{ events?: WsTaskEvent[] }>(`/tasks/${id}/events`);
    eventsCache.set(id, r.events || []);
  } catch (e) { eventsCache.set(id, []); }
  // gap 1: the authoritative single-task read. The panel no longer depends on
  // the board list carrying this row — a cross-client My-Work / deep-link open
  // renders from here even when ws.tasks doesn't include the task. When the row
  // IS on the board, renderPanel still prefers the (live, viewer-annotated)
  // ws.tasks copy; this refreshes `lastTask` as the fallback.
  try {
    const r = await ws.api<{ task?: WsTask }>(`/clients/${ws.clientSlug}/tasks/${id}`);
    if (r.task && openTaskId === id) lastTask = r.task;
  } catch (e) { /* stale deep link / gone task — the board fallback still paints */ }
  // gap 3: the active-watcher roster (identities, not just a count).
  try {
    const r = await ws.api<{ watchers?: WsWatcher[] }>(`/clients/${ws.clientSlug}/tasks/${id}/watchers`);
    watchersCache.set(id, r.watchers || []);
  } catch (e) { watchersCache.set(id, []); }
  const t = ws.tasks.find((x) => x.id === id) || lastTask;
  if (t) {
    const items = await ws.fetchChecklist(t);
    checklistCache.set(id, items);
    // keep the row's {done,total} counter honest for the next board repaint
    t.checklist = { done: items.filter((i) => i.done).length, total: items.length };
  }
  if (openTaskId === id) renderPanel(ws);
}

// re-fetch just the async parts after a note/checklist write, then re-render.
async function reloadDetail(ws: Workspace): Promise<void> {
  if (openTaskId == null) return;
  await loadDetail(ws, openTaskId);
}

// ── the ONE guarded transition entry (mirrors statusMenu's reason/note
//    collection, then defers to the shared applyTransition — never a fork) ──
async function panelTransition(ws: Workspace, t: WsTask, to: string): Promise<void> {
  let reason: string | null = null;
  if (to === 'wont-do') {
    reason = await ws.ask({
      title: "Why won't this be done?", multiline: true,
      hint: 'Required — kept on the task record so the next run marks it known instead of re-flagging it.',
      ok: "Won't do",
    });
    if (!reason) return;
  } else if (to === 'attested') {
    // MANDATORY non-empty evidence note — the human-attestation path, recorded
    // distinct from a probe-verified result; a later recheck/run still overrides.
    reason = await ws.ask({
      title: 'Mark human-attested', multiline: true,
      hint: 'Required — describe the outside evidence (e.g. "client emailed a screenshot confirming the fix"). Recorded as a human attestation, kept distinct from a probe-verified result; a later recheck or run still overrides it either way.',
      ok: 'Attest',
    });
    if (!reason || !reason.trim()) return;
  }
  await applyTransition(ws, t, to, reason);
}

// the assign flow, identical to the board row's actClick 'assign' branch: pick
// a REGISTERED teammate (no phantom owners), then setField the resolved id.
function assignFlow(ws: Workspace, t: WsTask): void {
  void ws.ask({
    title: 'Assign to', value: t.assignee || ws.author() || '', placeholder: 'pick a teammate',
    options: ws.roster(), hint: 'Pick a teammate from the roster — an assignee must be a registered user.',
  }).then((v) => {
    if (v === null) return;
    const u = ws.resolveUser(v);
    if (!u) { ws.toast(`"${v}" isn't on the roster — pick a registered teammate (no free-typed owners).`, 'warn'); return; }
    ws.setField(t, 'assignee_user_id', u.id);
  });
}

// ── content edits (Task-API completion gap 2) ────────────────────────────────
// SEMANTICS, enforced server-side and mirrored in the affordances the panel
// shows: on an AM-created CUSTOM task title/detail are directly editable; on a
// research-DERIVED task they are derivation-owned (a re-run clobbers them), so
// the panel offers NO title/detail edit there — only the AM annotation
// (detail_note), which is kept alongside the derived description and never
// touched by reconcile. Every write rides ws.setField → PATCH /tasks/{id},
// which surfaces a server 422/403 as an honest warn toast (never silent) and
// never touches status / status_changed_at (the stall clock is untouched by an
// edit — a server invariant this UI relies on, not one it can violate).
function isCustomTask(t: WsTask): boolean {
  return t.action_key.startsWith('custom:');
}

function editTitle(ws: Workspace, t: WsTask): void {
  void ws.ask({
    title: 'Rename task', value: t.title,
    hint: 'The task title. Renaming never changes its identity or moves the stall clock.',
    ok: 'Save',
  }).then((v) => {
    if (v === null) return;
    void ws.setField(t, 'title', v.trim());
  });
}

function editDetail(ws: Workspace, t: WsTask): void {
  void ws.ask({
    title: t.detail ? 'Edit description' : 'Add a description', value: t.detail || '',
    multiline: true, hint: 'The task description.', ok: 'Save',
  }).then((v) => {
    if (v === null) return;
    void ws.setField(t, 'detail', v);
  });
}

function editAnnotation(ws: Workspace, t: WsTask): void {
  void ws.ask({
    title: t.detail_note ? 'Edit your note' : 'Add a note', value: t.detail_note || '',
    multiline: true,
    hint: 'Your annotation — kept alongside the derived description and never overwritten by a re-run. Clear it to remove.',
    ok: 'Save',
  }).then((v) => {
    if (v === null) return;
    void ws.setField(t, 'detail_note', v);
  });
}

// ── section renderers ───────────────────────────────────────────────────────
function fmtAt(iso?: string | null): string {
  return (iso || '').slice(0, 16).replace('T', ' ');
}

// The title / description / annotation area, edit affordances gated by task
// TYPE (gap 2). A CUSTOM task's title + detail carry inline edit buttons; a
// DERIVED task shows them read-only (derivation-owned) plus the AM annotation
// (detail_note) with its own edit affordance. A 422/403 from the server is an
// honest warn toast via ws.setField — never a silent no-op.
function contentHeader(ws: Workspace, t: WsTask): TemplateResult {
  const custom = isCustomTask(t);
  return html`
    <h2 class="td-title">
      <span class="td-title-t">${t.title}</span>
      ${custom
        ? html`<button class="td-edit" data-td-edit-title title="Rename this task" @click=${() => editTitle(ws, t)}>Rename</button>`
        : nothing}
    </h2>
    ${t.detail
      ? html`<p class="td-detail">${t.detail}${custom ? html` <button class="td-edit" data-td-edit-detail title="Edit the description" @click=${() => editDetail(ws, t)}>Edit</button>` : nothing}</p>`
      : custom
        ? html`<button class="td-edit td-detail-add" data-td-edit-detail @click=${() => editDetail(ws, t)}>+ Add a description</button>`
        : nothing}
    ${custom ? nothing : annotationBlock(ws, t)}`;
}

// The AM annotation (detail_note) on a DERIVED task: an AM-owned aside kept
// beside the derivation-owned description, never overwritten by a re-run. The
// edit affordance is always present (add / edit / clear); the derived title +
// detail above stay read-only.
function annotationBlock(ws: Workspace, t: WsTask): TemplateResult {
  return html`
    <div class="td-annotation" data-td-annotation>
      <div class="td-annotation-h">
        <span>Your note</span>
        <button class="td-edit" data-td-edit-note title="An AM annotation kept alongside the derived description — never overwritten by a re-run" @click=${() => editAnnotation(ws, t)}>${t.detail_note ? 'Edit' : '+ Add'}</button>
      </div>
      ${t.detail_note
        ? html`<p class="td-annotation-body">${t.detail_note}</p>`
        : html`<p class="td-annotation-empty">No annotation yet — add your own note without touching the run-derived text.</p>`}
    </div>`;
}

function statusBlock(ws: Workspace, t: WsTask): TemplateResult {
  const legal = ws.transitions[t.status] || [];
  const probe = ws.probeFor(t);
  const r = t.lastRecheck;
  const moveLabel = (s: string): string =>
    s === 'wont-do' ? "Won't-do…" : s === 'attested' ? 'Mark human-attested…' : (ws.STATUS_FULL[s] || s);
  return html`
    <section class="td-sec td-status">
      <div class="td-sec-h">Status</div>
      <div class="td-status-row">
        <span class="wsst s-${t.status}" title="${ws.STATUS_FULL[t.status] || t.status}">${t.status === 'attested' ? ws.ICON.user : nothing}${ws.STATUS_LABEL[t.status] || t.status}</span>
        ${t.status === 'attested' && t.attested_by
          ? html`<span class="td-prov">Human-attested by <b>${t.attested_by}</b>${t.attested_at ? ' · ' + String(t.attested_at).slice(0, 10) : ''}${t.attested_note ? html` — “${t.attested_note}”` : ''} · a recheck or the next run still overrides this</span>`
          : nothing}
      </div>
      ${r ? html`<div class="td-prov">Last probe: ${r.verdict === 'observed' ? '✓ observed' : r.verdict === 'not-observed' ? '✗ not observed' : '? unavailable'}${r.at ? ' · ' + fmtAt(r.at) : ''}${r.verdict === 'not-observed' && t.status === 'attested' ? ' — contradicts the attestation; do not export as verified' : ''}</div>` : nothing}
      <div class="td-moves">
        ${legal.length
          ? legal.map((s) => html`<button class="wsmini td-move" data-to="${s}" @click=${() => panelTransition(ws, t, s)}>${moveLabel(s)}</button>`)
          : html`<span class="td-prov">No manual moves from here.</span>`}
        ${probe ? html`<button class="wsmini td-recheck" data-td-recheck title="${probe.hint}" @click=${(ev: MouseEvent) => ws.recheck(t, ev.currentTarget as HTMLButtonElement)}>${ws.ICON.recheck}Recheck</button>` : nothing}
      </div>
      <div class="td-note td-status-note">Verified &amp; Regressed are evidence-only — they arrive from a recheck probe or the next research run, never a click. Attesting needs the evidence note above.</div>
    </section>`;
}

// gap 3: the active-watcher roster as NAMES (identities from
// GET /tasks/{id}/watchers), not just the count on the toggle. `undefined`
// while the async fetch is in flight; a resolved empty list renders nothing
// (the toggle already shows "Watch"); the viewer's own row reads "you".
function watcherRoster(t: WsTask): TemplateResult {
  const roster = watchersCache.get(t.id);
  if (roster === undefined || roster.length === 0) return html`${nothing}`;
  return html`<span class="td-watchers" title="Watching: get regression / due / stall alerts">${roster.map((w) => w.name).join(', ')}</span>`;
}

function attributesBlock(ws: Workspace, t: WsTask): TemplateResult {
  const overdue = ws.overdueDays(t);
  const age = ws.stateAge(t);
  const waiting = /^waiting/.test(t.status);
  return html`
    <section class="td-sec">
      <div class="td-sec-h">Details</div>
      <dl class="td-kv">
        <dt>Owner</dt>
        <dd><button class="wsmini" @click=${() => assignFlow(ws, t)}>${ws.ICON.user}${t.assignee || 'Unassigned'}</button></dd>
        <dt>Watchers</dt>
        <dd>
          <button class="wsmini" aria-pressed=${t.watching ? 'true' : 'false'} title=${t.watching ? 'You’re watching — click to stop its alerts' : 'Watch to get regression / due / stall alerts'} style=${t.watching ? 'color:var(--azure)' : ''} @click=${async () => { await ws.toggleWatch(t); watchersCache.delete(t.id); void reloadDetail(ws); if (currentWs) renderPanel(currentWs); }}>${t.watching ? ws.ICON.eye : ws.ICON.eyeOff}${t.watching ? 'Watching' : 'Watch'}${t.watcherCount ? ' · ' + t.watcherCount : ''}</button>
          ${watcherRoster(t)}
        </dd>
        <dt>Priority</dt>
        <dd><button class="prio ${t.priority}" style="border:none;cursor:pointer" title="click to cycle priority" @click=${() => ws.setField(t, 'priority', t.priority === 'high' ? 'medium' : t.priority === 'medium' ? 'low' : 'high')}>${t.priority}</button></dd>
        <dt>Due</dt>
        <dd><button class="wsmini" @click=${() => ws.ask({ title: 'Due date', type: 'date', value: t.due_date || '' }).then((v) => ws.setField(t, 'due_date', v))}>${ws.ICON.cal}${t.due_date || 'set a due date'}</button>${overdue > 0 ? html` <span class="wsage late">⚠ ${overdue}d late</span>` : nothing}</dd>
        ${waiting ? html`<dt>Waiting</dt><dd><span class="wsage ${age > 14 ? 'hot' : ''}">${age}d in ${ws.STATUS_FULL[t.status] || t.status}</span></dd>` : nothing}
        <dt>Program</dt>
        <dd><button class="wsmini" title="part of the agreed client program" @click=${() => ws.setField(t, 'in_program', !t.in_program)}>${t.in_program ? ws.ICON.starFill : ws.ICON.star}${t.in_program ? 'In the agreed program' : 'Not in program'}</button></dd>
        <dt>Category</dt><dd>${t.category || '—'}${t.effort ? ' · ' + t.effort + ' effort' : ''}</dd>
      </dl>
    </section>`;
}

// derivation source + probe history + attestation + reconcile — the "evidence
// story in one place" the benchmark asks for (rendered from event history +
// task fields; nothing invented).
function evidenceBlock(ws: Workspace, t: WsTask): TemplateResult {
  const evs = eventsCache.get(t.id);
  const rechecks = (evs || []).filter((e) => e.kind === 'recheck');
  const reconciles = (evs || []).filter((e) => e.kind === 'reconcile');
  const derived = t.action_key.startsWith('custom:')
    ? 'AM-created custom task'
    : `Derived from a research run — stable identity across re-runs`;
  return html`
    <section class="td-sec">
      <div class="td-sec-h">Evidence &amp; provenance</div>
      <div class="td-ev-line"><span class="td-ev-k">Origin</span> ${derived}</div>
      ${t.status === 'attested' && t.attested_by
        ? html`<div class="td-ev-line"><span class="td-ev-k">Attestation</span> ${ws.ICON.user} ${t.attested_by}${t.attested_at ? ' · ' + String(t.attested_at).slice(0, 10) : ''}${t.attested_note ? html` — “${t.attested_note}”` : ''}</div>`
        : nothing}
      ${evs === undefined
        ? html`<div class="td-prov">loading evidence…</div>`
        : html`
          ${rechecks.length
            ? html`<div class="td-ev-sub">Probe history</div>${rechecks.slice().reverse().map((e) => html`<div class="td-ev-line"><span class="td-ev-k">${fmtAt(e.at)}</span> ${e.body || 'recheck'}${e.author ? ' · ' + e.author : ''}</div>`)}`
            : html`<div class="td-prov">No verification probe has run on this task yet.</div>`}
          ${reconciles.length
            ? html`<div class="td-ev-sub">Reconcile</div>${reconciles.slice().reverse().map((e) => html`<div class="td-ev-line"><span class="td-ev-k">${fmtAt(e.at)}</span> ${e.to_status ? html`<b>${ws.STATUS_LABEL[e.from_status ?? ''] || e.from_status || ''} → ${ws.STATUS_LABEL[e.to_status] || e.to_status}</b>` : ''} ${e.body || ''}</div>`)}`
            : nothing}`}
    </section>`;
}

function notesBlock(ws: Workspace, t: WsTask): TemplateResult {
  const notes = ws.notes.filter((n) => n.target_type === 'task' && String(n.target_id) === String(t.id) && !n.resolved_at);
  const save = async (btn: HTMLElement) => {
    const ta = btn.closest('.td-note-compose')?.querySelector('textarea') as HTMLTextAreaElement | null;
    const body = ta?.value.trim();
    if (!body) return;
    try {
      await ws.api(`/clients/${ws.clientSlug}/notes`, {
        method: 'POST', body: JSON.stringify({ body, target_type: 'task', target_id: String(t.id) }),
      });
      ws.toast('Note saved.', 'ok');
      if (ta) ta.value = '';
      try { ws.notes = ((await ws.api<{ notes?: WsNote[] }>(`/clients/${ws.clientSlug}/notes`)).notes) || []; } catch (e) {}
      await reloadDetail(ws);
    } catch (e) { ws.toast('Note failed: ' + (e as Error).message, 'warn'); }
  };
  return html`
    <section class="td-sec">
      <div class="td-sec-h">Notes &amp; comments${notes.length ? html` <span class="pill-meta">${notes.length}</span>` : nothing}</div>
      ${notes.length
        ? notes.map((n) => html`<div class="td-comment"><div class="td-comment-body">${renderMentionBody(n.body)}</div><div class="td-comment-meta">${n.author || ''}${n.created_at ? ' · ' + fmtAt(n.created_at) : ''}</div></div>`)
        : html`<div class="td-prov">No notes yet — start the thread below.</div>`}
      <div class="td-note-compose">
        <textarea rows="2" placeholder="add a note for the team… type @ to mention a teammate" @input=${(ev: Event) => mentionInput(ev, ws)} @keydown=${(ev: KeyboardEvent) => mentionKeydown(ev)} @blur=${() => mentionBlur()}></textarea>
        <button class="wsmini" data-td-save @click=${(ev: MouseEvent) => save(ev.currentTarget as HTMLElement)}>Save note</button>
      </div>
    </section>`;
}

function activityBlock(t: WsTask, ws: Workspace): TemplateResult {
  const evs = eventsCache.get(t.id);
  return html`
    <section class="td-sec">
      <div class="td-sec-h">Activity</div>
      ${evs === undefined
        ? html`<div class="td-prov">loading activity…</div>`
        : evs.length
          ? html`<div class="td-activity">${repeat(evs.slice().reverse(), (e, i) => `${e.at}-${i}`, (e) => html`
              <div class="td-act"><span class="td-act-when">${fmtAt(e.at)}${e.author ? ' · ' + e.author : ''}</span>
              ${e.kind === 'status' || (e.kind === 'reconcile' && e.to_status)
                ? html`<b>${ws.STATUS_LABEL[e.from_status ?? ''] || e.from_status || 'start'} → ${ws.STATUS_LABEL[e.to_status ?? ''] || e.to_status}</b>`
                : html`<span class="td-act-kind">${e.kind || 'event'}</span>`}
              ${e.body ? html` · ${renderMentionBody(e.body)}` : ''}</div>`)}</div>`
          : html`<div class="td-prov">No activity recorded yet.</div>`}
    </section>`;
}

// ── the whole panel ─────────────────────────────────────────────────────────
function renderPanel(ws: Workspace): void {
  if (openTaskId == null || !host) return;
  const t = ws.tasks.find((x) => x.id === openTaskId) || lastTask;
  if (!t) { render(html`<div class="td-empty">This task is no longer on the board.</div>`, host.body); return; }
  lastTask = t;
  const brand = payloadStore.get().brand || '';
  const items = checklistCache.get(t.id) ?? [];
  render(html`
    <header class="td-head">
      <div class="td-head-meta">
        <span class="td-key" title="typed task identity — stable across re-runs">${t.action_key}</span>
        ${brand ? html`<span class="td-client">${brand}</span>` : nothing}
      </div>
      <button class="td-close" aria-label="Close task detail" @click=${() => closeTaskPanel()}>✕</button>
    </header>
    ${contentHeader(ws, t)}
    ${statusBlock(ws, t)}
    ${attributesBlock(ws, t)}
    <section class="td-sec">${labelEditor(ws, t, () => { if (currentWs) renderPanel(currentWs); })}</section>
    <section class="td-sec">${checklistPanel(ws, t, items, () => { void reloadDetail(ws); })}</section>
    ${evidenceBlock(ws, t)}
    ${notesBlock(ws, t)}
    ${activityBlock(t, ws)}
  `, host.body);
}
